import { i as isBrowserScope, d as debounce, c as createWrapperWithEmitter, g as getOrSet, a as globalInstance, b as globalScope, e as contextId, f as isIE, w as windowAddEventListener, S as SESSION_EVENTS, h as SESSION_EVENT_TYPES, j as generateRandomHexString, D as DEFAULT_EXPIRES_MS, k as DEFAULT_INACTIVE_MS, l as getModeledObject, P as PREFIX, m as warn, n as handle, F as FEATURE_NAMES, o as SUPPORTABILITY_METRIC_CHANNEL, M as MODE, p as getRuntime, q as getConfiguration, r as DEFAULT_KEY, s as getInfo, t as defaultRegister, u as drain } from './micro-agent-8d9a9dc2.js';
import { s as stringify } from './stringify-d8602ca9.js';
import { s as subscribeToVisibilityChange } from './page-visibility-48795b15.js';

class Timer {
  constructor(opts, ms) {
    if (!opts.onEnd) throw new Error('onEnd handler is required');
    if (!ms) throw new Error('ms duration is required');
    this.onEnd = opts.onEnd;
    this.initialMs = ms;
    this.startTimestamp = Date.now();
    this.timer = this.create(this.onEnd, ms);
  }
  create(cb, ms) {
    if (this.timer) this.clear();
    return setTimeout(() => cb ? cb() : this.onEnd(), ms || this.initialMs);
  }
  clear() {
    clearTimeout(this.timer);
    this.timer = null;
  }
  end() {
    this.clear();
    this.onEnd();
  }
  isValid() {
    return this.initialMs - (Date.now() - this.startTimestamp) > 0;
  }
}

class InteractionTimer extends Timer {
  constructor(opts, ms) {
    super(opts, ms);
    this.onPause = typeof opts.onPause === 'function' ? opts.onPause : () => {/* noop */};
    this.onRefresh = typeof opts.onRefresh === 'function' ? opts.onRefresh : () => {/* noop */};
    this.onResume = typeof opts.onResume === 'function' ? opts.onResume : () => {/* noop */};

    /** used to double-check LS state at resume time */
    this.readStorage = opts.readStorage;

    // used by pause/resume
    this.remainingMs = undefined;
    if (!opts.refreshEvents) opts.refreshEvents = ['click', 'keydown', 'scroll'];

    // the abort controller is used to "reset" the event listeners and prevent them from duplicating when new sessions are created
    try {
      this.abortController = new AbortController();
    } catch (e) {
      // this try-catch can be removed when IE11 is completely unsupported & gone
    }
    if (isBrowserScope && opts.ee) {
      if (opts.ee) {
        this.ee = opts.ee;
        const debouncedRefresh = debounce(this.refresh.bind(this), 500, {
          leading: true
        });
        this.refreshHandler = evts => {
          if (opts.refreshEvents.includes(evts?.[0]?.type)) {
            debouncedRefresh();
          }
        };
        opts.ee.on('fn-end', this.refreshHandler);
      }

      // watch for the vis state changing.  If the page is hidden, the local inactivity timer should be paused
      // if the page is brought BACK to visibility and the timer hasnt "naturally" expired, refresh the timer...
      // this is to support the concept that other tabs could be experiencing activity.  The thought would be that
      // "backgrounded" tabs would pause, while "closed" tabs that "reopen" will just instantiate a new SessionEntity class if restored
      // which will do a "hard" check of the timestamps.

      // NOTE -- this does not account for 2 browser windows open side by side, blurring/focusing between them
      // IF DEEMED necessary, more event handling would be needed to account for this.
      subscribeToVisibilityChange(state => {
        if (state === 'hidden') this.pause();
        // vis change --> visible is treated like a new interaction with the page
        else this.resume();
      }, false, false, this.abortController?.signal);
    }
  }
  abort() {
    this.clear();
    this.abortController?.abort();
    if (this.refreshHandler) {
      this.ee.removeEventListener('fn-end', this.refreshHandler);
      this.refreshHandler = this.ee = null;
    }
  }
  pause() {
    this.onPause();
    clearTimeout(this.timer);
    this.remainingMs = this.initialMs - (Date.now() - this.startTimestamp);
  }
  resume() {
    try {
      const lsData = this.readStorage();
      const obj = typeof lsData === 'string' ? JSON.parse(lsData) : lsData;
      if (isExpired(obj.expiresAt) || isExpired(obj.inactiveAt)) this.end();else {
        this.refresh();
        this.onResume(); // emit resume event after state updated
      }
    } catch (err) {
      this.end();
    }
    function isExpired(timestamp) {
      return Date.now() > timestamp;
    }
  }
  refresh(cb, ms) {
    this.clear();
    this.timer = this.create(cb, ms);
    this.startTimestamp = Date.now();
    this.remainingMs = undefined;
    this.onRefresh();
  }
}

/*
 * Copyright 2020 New Relic Corporation. All rights reserved.
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * @file Wraps `addEventListener` and `removeEventListener` for instrumentation.
 * This module is used directly by: session_trace.
 * It is also called by -> wrapXhr <-, so see "wrap-xhr.js" for features that use this indirectly.
 */
const wrapped = {};
const XHR = globalScope.XMLHttpRequest;
const ADD_EVENT_LISTENER = 'addEventListener';
const REMOVE_EVENT_LISTENER = 'removeEventListener';
const flag = "nr@wrapped:".concat(contextId);

/**
 * Wraps `addEventListener` and `removeEventListener` on: global scope; the prototype of `XMLHttpRequest`, and
 * `document` (if in a browser scope). Adds custom events in context of a new emitter scoped only to these methods.
 * @param {Object} sharedEE - The shared event emitter on which a new scoped
 *     event emitter will be based.
 * @returns {Object} Scoped event emitter with a debug ID of `events`.
 */
function wrapEvents(sharedEE) {
  var ee = scopedEE(sharedEE);

  // Notice if our wrapping never ran yet, the falsy NaN will not early return; but if it has,
  // then we increment the count to track # of feats using this at runtime.
  if (wrapped[ee.debugId]++) return ee;
  wrapped[ee.debugId] = 1; // otherwise, first feature to wrap events
  var wrapFn = createWrapperWithEmitter(ee, true);

  // Guard against instrumenting environments w/o necessary features
  if ('getPrototypeOf' in Object) {
    if (isBrowserScope) findEventListenerProtoAndCb(document, wrapNode);
    findEventListenerProtoAndCb(globalScope, wrapNode);
    findEventListenerProtoAndCb(XHR.prototype, wrapNode);
  }
  ee.on(ADD_EVENT_LISTENER + '-start', function (args, target) {
    var originalListener = args[1];
    if (originalListener === null || typeof originalListener !== 'function' && typeof originalListener !== 'object') {
      return;
    }
    var wrapped = getOrSet(originalListener, flag, function () {
      var listener = {
        object: wrapHandleEvent,
        function: originalListener
      }[typeof originalListener];
      return listener ? wrapFn(listener, 'fn-', null, listener.name || 'anonymous') : originalListener;
      function wrapHandleEvent() {
        if (typeof originalListener.handleEvent !== 'function') return;
        return originalListener.handleEvent.apply(originalListener, arguments);
      }
    });
    this.wrapped = args[1] = wrapped;
  });
  ee.on(REMOVE_EVENT_LISTENER + '-start', function (args) {
    args[1] = this.wrapped || args[1];
  });
  function wrapNode(node) {
    wrapFn.inPlace(node, [ADD_EVENT_LISTENER, REMOVE_EVENT_LISTENER], '-', uniqueListener);
  }
  function uniqueListener(args, obj) {
    // Context for the listener is stored on itself.
    return args[1];
  }
  return ee;
}
/**
 * Find the base prototype of 'object' that has its own "addEventListener" property, and run some function on it.
 * @param {Object} object - the initial object to traverse prototype chain on
 * @param {Function} cb - the function to run on the ancestral object once found, accepts an object as a arg
 * @param {Array} rest - [optional] any additional arguments to pass to the cb
 */
function findEventListenerProtoAndCb(object, cb) {
  let step = object;
  while (typeof step === 'object' && !Object.prototype.hasOwnProperty.call(step, ADD_EVENT_LISTENER)) {
    step = Object.getPrototypeOf(step);
  }
  for (var _len = arguments.length, rest = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    rest[_key - 2] = arguments[_key];
  }
  if (step) cb(step, ...rest);
}

/**
 * Returns an event emitter scoped specifically for the `events` context. This scoping is a remnant from when all the
 * features shared the same group in the event, to isolate events between features. It will likely be revisited.
 * @param {Object} sharedEE - Optional event emitter on which to base the scoped emitter.
 *     Uses `ee` on the global scope if undefined).
 * @returns {Object} Scoped event emitter with a debug ID of 'events'.
 */
function scopedEE(sharedEE) {
  return (sharedEE || globalInstance).get('events');
}

// this is what can be stored in local storage (not enforced but probably should be)
// these values should sync between local storage and the parent class props
const model = {
  value: '',
  inactiveAt: 0,
  expiresAt: 0,
  updatedAt: Date.now(),
  sessionReplayMode: MODE.OFF,
  sessionReplaySentFirstChunk: false,
  sessionTraceMode: MODE.OFF,
  traceHarvestStarted: false,
  serverTimeDiff: null,
  // set by TimeKeeper; "undefined" value will not be stringified and stored but "null" will
  custom: {}
};
class SessionEntity {
  /**
   * Create a self-managing Session Entity. This entity is scoped to the agent identifier which triggered it, allowing for multiple simultaneous session objects to exist.
   * There is one "namespace" an agent can store data in LS -- NRBA_{key}. If there are two agents on one page, and they both use the same key, they could overwrite each other since they would both use the same namespace in LS by default.
   * The value can be overridden in the constructor, but will default to a unique 16 character hex string
   * expiresMs and inactiveMs are used to "expire" the session, but can be overridden in the constructor. Pass 0 to disable expiration timers.
   */
  constructor(opts) {
    const {
      agentIdentifier,
      key,
      storage
    } = opts;
    if (!agentIdentifier || !key || !storage) {
      throw new Error("Missing required field(s):".concat(!agentIdentifier ? ' agentID' : '').concat(!key ? ' key' : '').concat(!storage ? ' storage' : ''));
    }
    this.agentIdentifier = agentIdentifier;
    this.storage = storage;
    this.state = {};

    // key is intended to act as the k=v pair
    this.key = key;
    this.ee = globalInstance.get(agentIdentifier);
    wrapEvents(this.ee);
    this.setup(opts);

    /**
     * Do not emit session storage events for IE11, because IE11 is unable to determine
     * if the event was spawned on the current page or an adjacent page, and the behavior tied
     * to storage events is critical to apply only to cross-tab behavior
     * */
    if (isBrowserScope && !isIE) {
      windowAddEventListener('storage', event => {
        if (event.key === this.lookupKey) {
          const obj = typeof event.newValue === 'string' ? JSON.parse(event.newValue) : event.newValue;
          this.sync(obj);
          this.ee.emit(SESSION_EVENTS.UPDATE, [SESSION_EVENT_TYPES.CROSS_TAB, this.state]);
        }
      });
    }
  }
  setup(_ref) {
    let {
      value = generateRandomHexString(16),
      expiresMs = DEFAULT_EXPIRES_MS,
      inactiveMs = DEFAULT_INACTIVE_MS
    } = _ref;
    /** Ensure that certain properties are preserved across a reset if already set */
    const persistentAttributes = {
      serverTimeDiff: this.state.serverTimeDiff || model.serverTimeDiff
    };
    this.state = {};
    this.sync({
      ...model,
      ...persistentAttributes
    });

    // value is intended to act as the primary value of the k=v pair
    this.state.value = value;
    this.expiresMs = expiresMs;
    this.inactiveMs = inactiveMs;

    // the first time the session entity class is instantiated, we check the storage API for an existing
    // object. If it exists, the values inside the object are used to inform the timers that run locally.
    // if the initial read is empty, it allows us to set a "fresh" "new" session immediately.
    // the local timers are used after the session is running to "expire" the session, allowing for pausing timers etc.
    // the timestamps stored in the storage API can be checked at initial run, and when the page is restored, otherwise we lean
    // on the local timers to expire the session
    const initialRead = this.read();

    // the set-up of the timer used to expire the session "naturally" at a certain time
    // this gets ignored if the value is falsy, allowing for session entities that do not expire
    if (expiresMs) {
      this.state.expiresAt = initialRead?.expiresAt || this.getFutureTimestamp(expiresMs);
      this.expiresTimer = new Timer({
        // When the inactive timer ends, collect a SM and reset the session
        onEnd: () => {
          this.collectSM('expired');
          this.collectSM('duration');
          this.reset();
        }
      }, this.state.expiresAt - Date.now());
    } else {
      this.state.expiresAt = Infinity;
    }

    // the set-up of the timer used to expire the session due to "inactivity" at a certain time
    // this gets ignored if the value is falsy, allowing for session entities that do not expire
    // this gets "refreshed" when "activity" is observed
    if (inactiveMs) {
      this.state.inactiveAt = initialRead?.inactiveAt || this.getFutureTimestamp(inactiveMs);
      this.inactiveTimer = new InteractionTimer({
        // When the inactive timer ends, collect a SM and reset the session
        onEnd: () => {
          this.collectSM('inactive');
          this.collectSM('duration');
          this.reset();
        },
        // When the inactive timer refreshes, it will update the storage values with an update timestamp
        onRefresh: this.refresh.bind(this),
        onResume: () => {
          this.ee.emit(SESSION_EVENTS.RESUME);
        },
        // When the inactive timer pauses, update the storage values with an update timestamp
        onPause: () => {
          if (this.initialized) this.ee.emit(SESSION_EVENTS.PAUSE);
          this.write(getModeledObject(this.state, model));
        },
        ee: this.ee,
        refreshEvents: ['click', 'keydown', 'scroll'],
        readStorage: () => this.storage.get(this.lookupKey)
      }, this.state.inactiveAt - Date.now());
    } else {
      this.state.inactiveAt = Infinity;
    }

    // The fact that the session is "new" or pre-existing is used in some places in the agent.  Session Replay and Trace
    // can use this info to inform whether to trust a new sampling decision vs continue a previous tracking effort.
    /* [NR-230914] 02/2024 - the logical OR assignment is used so that isNew remains 'true' if it was already set as such. This fixes the expires and inactive timestamps timing out in localStorage
      while no page for a given domain is in-use and the session resetting upon user returning to the page as part of a fresh session. */
    this.isNew ||= !Object.keys(initialRead).length;
    // if its a "new" session, we write to storage API with the default values.  These values may change over the lifespan of the agent run.
    // we can use a modeled object here to help us know and manage what values are being used. -- see "model" above
    if (this.isNew) this.write(getModeledObject(this.state, model), true);else this.sync(initialRead);
    this.initialized = true;
    this.ee.emit(SESSION_EVENTS.STARTED, [this.isNew]);
  }

  // This is the actual key appended to the storage API
  get lookupKey() {
    return "".concat(PREFIX, "_").concat(this.key);
  }
  sync(data) {
    Object.assign(this.state, data);
  }

  /**
   * Fetch the stored values from the storage API tied to this entity
   * @returns {Object}
   */
  read() {
    try {
      const val = this.storage.get(this.lookupKey);
      if (!val) return {};
      // TODO - decompression would need to happen here if we decide to do it
      const obj = typeof val === 'string' ? JSON.parse(val) : val;
      if (this.isInvalid(obj)) return {};
      // if the session expires, collect a SM count before resetting
      if (this.isExpired(obj.expiresAt)) {
        this.collectSM('expired');
        this.collectSM('duration', obj, true);
        return this.reset();
      }
      // if "inactive" timer is expired at "read" time -- esp. initial read -- reset
      // collect a SM count before resetting
      if (this.isExpired(obj.inactiveAt)) {
        this.collectSM('inactive');
        this.collectSM('duration', obj, true);
        return this.reset();
      }
      return obj;
    } catch (e) {
      warn(10, e);
      // storage is inaccessible
      return {};
    }
  }

  /**
   * Store data to the storage API tied to this entity
   * To preseve existing attributes, the output of ...session.read()
   * should be appended to the data argument
   * @param {Object} data
   * @returns {Object}
   */
  write(data) {
    try {
      if (!data || typeof data !== 'object') return;
      // everytime we update, we can update a timestamp for sanity
      data.updatedAt = Date.now();
      this.sync(data); // update the parent class "state" properties with the local storage values
      //
      // TODO - compression would need happen here if we decide to do it
      this.storage.set(this.lookupKey, stringify(this.state));
      this.ee.emit(SESSION_EVENTS.UPDATE, [SESSION_EVENT_TYPES.SAME_TAB, this.state]);
      return data;
    } catch (e) {
      // storage is inaccessible
      warn(11, e);
      return null;
    }
  }
  reset() {
    // this method should set off a chain of actions across the features by emitting 'new-session'
    // * send off pending payloads
    // * stop recording (stn and sr)...
    // * delete the session and start over
    try {
      if (this.initialized) this.ee.emit(SESSION_EVENTS.RESET);
      this.storage.remove(this.lookupKey);
      this.inactiveTimer?.abort?.();
      this.expiresTimer?.clear?.();
      delete this.isNew;
      this.setup({
        agentIdentifier: this.agentIdentifier,
        key: this.key,
        storage: this.storage,
        expiresMs: this.expiresMs,
        inactiveMs: this.inactiveMs
      });
      return this.read();
    } catch (e) {
      return {};
    }
  }

  /**
   * Refresh the inactivity timer data
   */
  refresh() {
    // read here & invalidate
    const existingData = this.read();
    this.write({
      ...existingData,
      inactiveAt: this.getFutureTimestamp(this.inactiveMs)
    });
  }

  /**
   * @param {number} timestamp
   * @returns {boolean}
   */
  isExpired(timestamp) {
    return Date.now() > timestamp;
  }

  /**
   * @param {Object} data
   * @returns {boolean}
   */
  isInvalid(data) {
    const requiredKeys = Object.keys(model);
    return !requiredKeys.every(x => Object.keys(data).includes(x));
  }
  collectSM(type, data, useUpdatedAt) {
    let value, tag;
    if (type === 'duration') {
      value = this.getDuration(data, useUpdatedAt);
      tag = 'Session/Duration/Ms';
    }
    if (type === 'expired') tag = 'Session/Expired/Seen';
    if (type === 'inactive') tag = 'Session/Inactive/Seen';
    if (tag) handle(SUPPORTABILITY_METRIC_CHANNEL, [tag, value], undefined, FEATURE_NAMES.metrics, this.ee);
  }
  getDuration() {
    let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.state;
    let useUpdatedAt = arguments.length > 1 ? arguments[1] : undefined;
    const startingTimestamp = data.expiresAt - this.expiresMs;
    const endingTimestamp = !useUpdatedAt ? data.updatedAt : Date.now();
    return endingTimestamp - startingTimestamp;
  }

  /**
   * @param {number} futureMs - The number of ms to use to generate a future timestamp
   * @returns {number}
   */
  getFutureTimestamp(futureMs) {
    return Date.now() + futureMs;
  }
  syncCustomAttribute(key, value) {
    if (!isBrowserScope) return;
    if (value === null) {
      const curr = this.read();
      if (curr.custom) {
        delete curr.custom[key];
        this.write({
          ...curr
        });
      }
    } else {
      const curr = this.read();
      this.custom = {
        ...(curr?.custom || {}),
        [key]: value
      };
      this.write({
        ...curr,
        custom: this.custom
      });
    }
  }
}

class LocalStorage {
  get(key) {
    try {
      // localStorage strangely type-casts non-existing data to "null"...
      // Cast it back to undefined if it doesnt exist
      return localStorage.getItem(key) || undefined;
    } catch (err) {
      // Error is ignored
      return '';
    }
  }
  set(key, value) {
    try {
      if (value === undefined || value === null) return this.remove(key);
      return localStorage.setItem(key, value);
    } catch (err) {
      // Error is ignored
    }
  }
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      // Error is ignored
    }
  }
}

let ranOnce = 0;
function setupAgentSession(agentIdentifier) {
  const agentRuntime = getRuntime(agentIdentifier);
  if (ranOnce++) return agentRuntime.session;
  const sessionInit = getConfiguration(agentIdentifier).session;
  agentRuntime.session = new SessionEntity({
    agentIdentifier,
    key: DEFAULT_KEY,
    storage: new LocalStorage(),
    expiresMs: sessionInit?.expiresMs,
    inactiveMs: sessionInit?.inactiveMs
  });

  // Retrieve & re-add all of the persisted setCustomAttribute|setUserId k-v from previous page load(s), if any was stored.
  const customSessionData = agentRuntime.session.state.custom;
  const agentInfo = getInfo(agentIdentifier);
  if (customSessionData) {
    agentInfo.jsAttributes = {
      ...agentInfo.jsAttributes,
      ...customSessionData
    };
  }
  const sharedEE = globalInstance.get(agentIdentifier);

  // any calls to newrelic.setCustomAttribute(<persisted>) will need to be added to:
  // local info.jsAttributes {}
  // the session's storage API
  defaultRegister('api-setCustomAttribute', (time, key, value) => {
    agentRuntime.session.syncCustomAttribute(key, value);
  }, 'session', sharedEE);

  // any calls to newrelic.setUserId(...) will need to be added to:
  // local info.jsAttributes {}
  // the session's storage API
  defaultRegister('api-setUserId', (time, key, value) => {
    agentRuntime.session.syncCustomAttribute(key, value);
  }, 'session', sharedEE);
  drain(agentIdentifier, 'session');
  return agentRuntime.session;
}

export { setupAgentSession };
//# sourceMappingURL=agent-session-59496e5e.js.map
